import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Database, BarChart3, TrendingUp, LineChart, PieChart, ArrowRight } from 'lucide-react';

const DataPlatforms = () => {
  const services = [
    {
      icon: Database,
      title: 'Big Data Platforms',
      description: 'Hadoop, Spark, Kafka-based systems for processing massive datasets. Enterprise-scale data infrastructure.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop'
    },
    {
      icon: TrendingUp,
      title: 'Real-Time Analytics',
      description: 'Live dashboards, streaming data pipelines. See what\'s happening now, not yesterday.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop'
    },
    {
      icon: BarChart3,
      title: 'BI Dashboards',
      description: 'Executive dashboards, operational KPIs, custom reports. Data visualization for decision makers.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop'
    },
    {
      icon: Database,
      title: 'Data Lakes',
      description: 'Centralized repositories for structured and unstructured data. Store everything, analyze anything.',
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&h=400&fit=crop'
    },
    {
      icon: LineChart,
      title: 'AI-Driven Reporting',
      description: 'Automated insights, anomaly detection, predictive alerts. AI reads your data and tells you what matters.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop'
    },
    {
      icon: PieChart,
      title: 'Predictive Analytics',
      description: 'Forecast revenue, demand, churn, inventory. Machine learning models for business prediction.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-900 via-teal-900 to-cyan-950 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920&h=800&fit=crop" 
            alt="Data" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Database className="h-16 md:h-20 w-16 md:w-20 mx-auto mb-6 text-green-300 animate-float" />
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">Data Platforms & Enterprise Intelligence</h1>
            <p className="text-lg md:text-xl text-green-200 mb-8">
              We convert raw data into live dashboards, executive insights and predictive business intelligence. Make data-driven decisions with confidence.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-green-900 hover:bg-green-50 text-base md:text-lg px-6 md:px-8 transition-all hover:scale-105">
                Transform Your Data
              </Button>
            </Link>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Data & Analytics Solutions</h2>
            <div className="w-24 h-1 bg-green-600 mx-auto mb-6"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 overflow-hidden group">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={service.title} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                        <IconComponent className="h-6 w-6 text-green-600" />
                      </div>
                    </div>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-green-900 to-cyan-950 text-white">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Unlock Your Data\'s Potential</h2>
          <p className="text-lg md:text-xl mb-8 text-green-100 max-w-2xl mx-auto">
            Turn raw data into actionable intelligence.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-green-900 hover:bg-green-50 text-lg px-8 transition-all hover:scale-105">
              Get Started
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default DataPlatforms;